/**
 * `wayai bases providers …` — move tool definitions between LLM providers and a
 * base's record types.
 *
 * Namespaced because "provider" already names a connector in WayAI's catalog.
 */

import { Command } from 'commander';
import { writeFileSync } from 'node:fs';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import {
  outputFormat,
  parseData,
  pathSegment,
  requireBase,
  splitList,
  withBaseOption,
} from '../helpers.js';
import { expected } from '../../lib/expected.js';

const VALID_PROVIDERS = ['openai', 'anthropic', 'google', 'mcp'] as const;
const VALID_PROVIDERS_HELP = VALID_PROVIDERS.join(', ');

/**
 * Validate the provider before it becomes a path segment.
 *
 * `pathSegment` alone would accept any slug and let the server 404, but the set
 * is closed and known here, so naming the four is a better error than a status
 * code — and it keeps the help text and the check from drifting apart.
 */
function providerSegment(provider: string): string {
  if (!(VALID_PROVIDERS as readonly string[]).includes(provider)) {
    throw expected(`Unknown provider ${JSON.stringify(provider)}. Expected one of: ${VALID_PROVIDERS_HELP}.`);
  }
  return pathSegment(provider, 'provider');
}

export function buildBasesProvidersCommand(): Command {
  // Every verb here addresses one base, so `--base` sits on the group.
  const providers = withBaseOption(new Command('providers')).description(
    'Import/export tool definitions between LLM providers and record types',
  );

  providers
    .command('import <provider>')
    .description(`Import tool definitions as record types. Providers: ${VALID_PROVIDERS_HELP}`)
    .requiredOption('--tools <json>', 'Tool definitions (inline JSON or @filename)')
    .action(async function (this: Command, provider: string, opts: { tools: string }) {
      const base = pathSegment(requireBase(this), '--base');
      const tools = parseData(opts.tools, '--tools');
      const client = await createDataClient();
      printOutput(
        await client.request('POST', `/v1/${base}/providers/${providerSegment(provider)}/import`, {
          tools: Array.isArray(tools) ? tools : [tools],
        }),
        outputFormat(this),
      );
    });

  providers
    .command('export <provider>')
    .description(`Export record types as tool definitions. Providers: ${VALID_PROVIDERS_HELP}`)
    .option('--record-types <ids>', 'Comma-separated record type ids (omit for all)')
    // `--to`, NOT `--output <file>`. The namespace root declares `--output
    // <format>`, and a parent's option is consumed by the PARENT's parse — so a
    // subcommand redeclaring the word never sees it, and `--output out.json`
    // here would set the namespace's format to "out.json" (falling back to the
    // table) while writing no file at all. `files get` hit the identical class
    // and was renamed the same way.
    .option('--to <file>', 'Write the exported definitions to a file instead of stdout')
    .action(async function (
      this: Command,
      provider: string,
      opts: { recordTypes?: string; to?: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const recordTypes = opts.recordTypes ? splitList(opts.recordTypes) : [];
      const query = recordTypes.length
        ? `?record_types=${encodeURIComponent(recordTypes.join(','))}`
        : '';
      const client = await createDataClient();
      const data = await client.request(
        'GET',
        `/v1/${base}/providers/${providerSegment(provider)}/export${query}`,
      );

      if (opts.to) {
        try {
          writeFileSync(opts.to, JSON.stringify(data, null, 2));
        } catch (e) {
          throw expected(`--to ${opts.to}: ${e instanceof Error ? e.message : 'could not be written'}`);
        }
        // Honors --json like every other action: a script piping this to `jq`
        // must not receive prose on stdout.
        printOutput({ written_to: opts.to }, outputFormat(this));
        return;
      }
      printOutput(data, outputFormat(this));
    });

  providers
    .command('import-call <provider> <record_type>')
    .description(
      `Create a record from a tool call in provider format. Providers: ${VALID_PROVIDERS_HELP}`,
    )
    .requiredOption('--data <json>', 'Tool call data in provider format (inline JSON or @filename)')
    .option('--external-id <id>', 'External id for idempotent upsert')
    .option('--external-source <source>', 'External source identifier')
    .action(async function (
      this: Command,
      provider: string,
      recordType: string,
      opts: { data: string; externalId?: string; externalSource?: string },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const callData = parseData(opts.data, '--data');
      const queryParts: string[] = [];
      if (opts.externalId) queryParts.push(`external_id=${encodeURIComponent(opts.externalId)}`);
      if (opts.externalSource) {
        queryParts.push(`external_source=${encodeURIComponent(opts.externalSource)}`);
      }
      const qs = queryParts.length ? `?${queryParts.join('&')}` : '';
      const client = await createDataClient();
      printOutput(
        await client.request(
          'POST',
          `/v1/${base}/providers/${providerSegment(provider)}/records/${pathSegment(recordType, 'record_type')}${qs}`,
          callData,
        ),
        outputFormat(this),
      );
    });

  return providers;
}
